import { NextPage } from 'next';
import { AppProps } from 'next/app';
import Script from 'next/script';
import { ReactElement, ReactNode } from 'react';
import { Provider } from 'react-redux';
import { useUserPathTracking } from '../hooks/useUserPathTracking';
import { useGclidTracking } from '../shared/useGclidTracking';
import { TAG_GATEWAY_PATH } from '../shared/tagGateway';
import { store } from '../store';
import '../styles/globals.scss';

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement, pageProps?: any) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page) => page);
  const canLoadThirdParties =
    process.env.NEXT_PUBLIC_ENVIRONMENT !== 'development';
  const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID as string;

  // Track user navigation path
  useUserPathTracking();

  // Capture gclid from URL on first page load
  useGclidTracking();

  return (
    <Provider store={store}>
      {getLayout(<Component {...pageProps} />, pageProps)}
      {/* Google Tag Manager, served first-party through the Google tag gateway.
          GA4 is loaded by the GTM container, not as a standalone tag. */}
      {canLoadThirdParties && (
        <Script id="google-tag-manager" strategy="afterInteractive">
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='${TAG_GATEWAY_PATH}/?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_ID}');`}
        </Script>
      )}
      {/* ZenDesk chat */}
      {canLoadThirdParties && (
        <Script
          strategy="lazyOnload"
          id="ze-snippet"
          src="https://static.zdassets.com/ekr/snippet.js?key=49e710b1-6324-464e-b276-6bfd27e3ea56"
        />
      )}
      <Script
        strategy="lazyOnload"
        id="trust-pilot-trust-box"
        src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
      />
    </Provider>
  );
}

export default MyApp;
